<script>
import { textOnColor } from '@modules/global/utils/Color'
import { getChipNewModuleProps } from '@utils/Format'

import IconNoModuleAccess from '@modules/global/components/icon-no-module-access/icon-no-module-access.vue'
import {
  SELECT_REPLAY_DURATION_MS,
  DETAILED_ANIMATION_ICONS,
} from './button-navbar.constants'

export default {
  name: 'ButtonNavbar',
  components: { IconNoModuleAccess },
  props: {
    selected: { type: Boolean, default: false },
    hasList: { type: Boolean, default: false },
    full: { type: Boolean, default: false },
    name: { type: String, default: '' },
    icon: { type: String, default: '' },
    value: { type: Object, default: () => {} },
    sm: { type: Boolean, default: false },
    isNew: { type: Boolean, default: false },
    noAccessIcon: { type: Boolean, default: false },
  },

  data() {
    return {
      arrowUp: false,
      isHover: false,
      justSelected: false,
    }
  },

  computed: {
    showEffect() {
      return this.selected || this.isHover
    },

    // Marca o ícone principal quando ele suporta a animação detalhada
    // (parte a parte). Vira a classe is-detailed no wrapper do icon-svg.
    isDetailedIcon() {
      return DETAILED_ANIMATION_ICONS.includes(this.icon)
    },

    // Gatilho único das animações do ícone: hover OU instante da seleção.
    // Unifica os dois estados numa classe (is-animating) para os seletores
    // CSS não precisarem repetir :hover e .nav-icon-just-selected em cada regra.
    iconAnimating() {
      return this.isHover || this.justSelected
    },
  },

  watch: {
    selected(isSelected, wasSelected) {
      // Item foi desmarcado: cancela o replay pendente e limpa o estado na
      // hora, para a classe nav-icon-just-selected nunca ficar presa num item
      // que já não está selecionado (troca rápida de seleção).
      if (!isSelected) {
        this.stopSelectAnimation()
        return
      }
      if (wasSelected) return
      this.playSelectAnimation()
    },
  },

  beforeDestroy() {
    this.clearSelectAnimTimer()
  },

  methods: {
    getChipNewModuleProps,

    clickEvent() {
      this.arrowUp = !this.arrowUp
      this.$emit('clickEvent', this.value)
    },

    clearSelectAnimTimer() {
      if (this.selectAnimTimer) clearTimeout(this.selectAnimTimer)
      this.selectAnimTimer = null
    },

    // Reinicia a classe para que a animação do ícone toque novamente a cada
    // (re)seleção, mesmo se o item já tiver sido selecionado antes.
    playSelectAnimation() {
      this.clearSelectAnimTimer()
      this.justSelected = false
      this.$nextTick(() => {
        this.justSelected = true
        this.selectAnimTimer = setTimeout(() => {
          this.justSelected = false
        }, SELECT_REPLAY_DURATION_MS)
      })
    },

    // Cancela o replay em andamento e reseta o estado imediatamente.
    stopSelectAnimation() {
      this.clearSelectAnimTimer()
      this.justSelected = false
    },

    textColorContrastNav() {
      const bg = this.$Color()['bg-navbar']

      const textColor = textOnColor(bg)

      const textOpacity = this.$setOpacity(textColor, 0.56)

      return this.selected ? textColor : textOpacity
    },

    bgColor() {
      const bgNavActive = this.$Color()['text-navbar-active']
      const colorOpacity = this.$setOpacity(bgNavActive, 0.4)

      return `radial-gradient(50% 50% at 50% 50%,${colorOpacity} 0%,transparent 100%)`
    },

    borderOpacity() {
      const primary = this.$Color()['primary']
      const colorOpacity = this.$setOpacity(primary, 0.3)

      return `0.5px solid ${colorOpacity}`
    },
  },
}
</script>

<template>
  <div
    class="d-flex full-center button-navbar"
    :class="{
      'button-navbar-size-default': !sm && full,
      'button-navbar-size-sm': sm && full,
      'button-navbar-size-hidden': !full,
      'nav-icon-selected': selected,
      'nav-icon-just-selected': justSelected,
    }"
    :style="{
      // border: showEffect ? borderOpacity() : '',
    }"
    style="position: relative"
    @click="clickEvent"
    @mouseover="isHover = true"
    @mouseleave="isHover = false"
  >
    <div
      style="width: 293px; height: 56px; position: absolute"
      :class="{ 'button-navbar-selected': showEffect }"
      :style="{
        background: showEffect ? bgColor() : '',
        left: full ? '-30px' : '-80px',
      }"
    ></div>

    <div
      style="
        position: absolute;
        width: 100%;
        height: 100%;
        z-index: 5;
        background-blend-mode: lighten;
      "
      :style="{
        background: showEffect ? '#ffffff08' : '',
      }"
      class="d-flex"
      :class="{
        'px-5': !sm,
        'px-3': sm,
      }"
    >
      <icon-svg
        v-if="icon && icon.length"
        class="navbar-main-icon"
        :class="{ 'is-detailed': isDetailedIcon, 'is-animating': iconAnimating }"
        :name="icon"
        :color="textColorContrastNav()"
        size="16"
      />
      <div
        v-if="full"
        class="d-flex align-items-center justify-content-between"
        :style="{ width: full ? '100%' : 'auto' }"
      >
        <p
          class="m-0"
          :style="{
            color: textColorContrastNav(),
          }"
          :class="{
            'pl-4': !sm,
          }"
        >
          {{ name }}
        </p>

        <div v-if="isNew || noAccessIcon" class="d-flex align-center">
          <IconNoModuleAccess
            v-if="noAccessIcon"
            iconSize="14"
            lockNavBarOnHover
            :module="name"
          />
          <v-chip v-if="isNew" v-bind="getChipNewModuleProps()"> Novo </v-chip>
        </div>

        <v-btn v-if="hasList" icon x-small>
          <icon-svg
            name="arrow-up-light"
            :color="textColorContrastNav()"
            size="12"
            :class="{
              'transform-rotate-0': !arrowUp && !selected,
              'transform-rotate-180': arrowUp && selected,
            }"
          />
        </v-btn>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
// ===== Animação dos ícones da navbar (hover + selecionado) =====
// Variáveis nomeadas para evitar magic numbers espalhados.
$nav-icon-hover-scale: 1.18
$nav-icon-selected-scale: 1.25
$nav-icon-grow-duration: .25s
// curva com overshoot → dá o "salto" ao crescer no selecionado
$nav-icon-grow-easing: cubic-bezier(.34, 1.56, .64, 1)
// durações das animações de "tell" por ícone
$nav-tell-spin: .7s
$nav-tell-spin-slow: .8s
$nav-tell-flip: .7s
$nav-tell-ring: .7s
$nav-tell-bounce: .5s
$nav-tell-bob: .55s
$nav-tell-tick: .45s
// ===== animação "detalhada" (parte a parte) — ícones com elementos repetidos
// duração de cada parte + atraso entre uma parte e a próxima (efeito cascata)
$nav-part-duration: .42s
$nav-part-stagger: .08s
// donut: tempo para "desenhar" o anel completo (0deg → 360deg)
$nav-donut-draw: .8s
// Financial Planning: "desenhar" a linha do gráfico (30% mais rápido que a
// versão inicial de .85s → .6s) + nós surgindo nas curvas.
$nav-chart-line-length: 34
$nav-chart-draw: .6s
$nav-chart-node: .21s
$nav-chart-node-step: .055s
// CRM: comprimento do conector (esconder antes de traçar) + tempo do traço
$nav-crm-link-length: 11
$nav-crm-draw: .3s

.button-navbar
  border-radius: 16px
  overflow: hidden

  p
    font-family: 'Inter'
    font-weight: 500

  &:hover
    cursor: pointer

  .button-navbar-selected
    backdrop-filter: blur(40px)
    opacity: .4

  // ícone principal (wrapper): scale do hover/selecionado vive AQUI; a
  // animação "tell" roda no svg interno → os dois transforms compõem.
  ::v-deep .navbar-main-icon
    transition: transform $nav-icon-grow-duration $nav-icon-grow-easing
    transform-origin: center
  &:hover ::v-deep .navbar-main-icon
    transform: scale($nav-icon-hover-scale)
  &.nav-icon-selected ::v-deep .navbar-main-icon
    transform: scale($nav-icon-selected-scale)

  // ===== Mapa de animações por ícone (is-animating = hover OU replay da
  // seleção; ::v-deep alcança o wrapper renderizado por icon-svg) =====
  // "Tell" de bloco (svg inteiro): cada ícone com seu movimento próprio
  ::v-deep .navbar-main-icon.is-animating.icon-setting svg
    animation: az-icon-spin $nav-tell-spin ease
  ::v-deep .navbar-main-icon.is-animating.icon-bell svg
    transform-origin: top center
    animation: az-icon-ring $nav-tell-ring ease
  ::v-deep .navbar-main-icon.is-animating.icon-profile-circular-frame svg,
  ::v-deep .navbar-main-icon.is-animating.icon-receipt-items svg
    animation: az-icon-bob $nav-tell-bob ease

  // --- Donut: "desenha" o anel (máscara cônica --donut-angle 0→360deg).
  // forwards trava em 360deg ao fim → anel inteiro visível, ícone não some.
  // Requer @property; sem suporte, ícone normal (sem máscara).
  ::v-deep .navbar-main-icon.is-animating.icon-donut
    -webkit-mask: conic-gradient(#000 var(--donut-angle), transparent 0deg)
    mask: conic-gradient(#000 var(--donut-angle), transparent 0deg)
    animation: az-donut-draw $nav-donut-draw ease forwards

  // ===== Animações "detalhadas" (parte a parte) =====
  // Cada <path> (parte repetida/sequencial) anima com atraso crescente.
  // transform-box: fill-box → transform-origin relativo ao próprio path.
  ::v-deep .navbar-main-icon.is-detailed.is-animating svg path
    transform-box: fill-box

  // --- Barras subindo (chart-bar-new-ux, chart-grow): 4 paths
  // path:1 = eixo/base | path:2,3,4 = barras → sobem da própria base
  ::v-deep .navbar-main-icon.is-animating.icon-chart-bar-new-ux svg path,
  ::v-deep .navbar-main-icon.is-animating.icon-chart-grow svg path
    transform-origin: bottom
    animation: az-icon-bar-rise $nav-part-duration cubic-bezier(.22, 1.2, .4, 1) both
  ::v-deep .navbar-main-icon.is-animating.icon-chart-bar-new-ux svg path:nth-child(2),
  ::v-deep .navbar-main-icon.is-animating.icon-chart-grow svg path:nth-child(2)
    animation-delay: $nav-part-stagger
  ::v-deep .navbar-main-icon.is-animating.icon-chart-bar-new-ux svg path:nth-child(3),
  ::v-deep .navbar-main-icon.is-animating.icon-chart-grow svg path:nth-child(3)
    animation-delay: $nav-part-stagger * 2
  ::v-deep .navbar-main-icon.is-animating.icon-chart-bar-new-ux svg path:nth-child(4),
  ::v-deep .navbar-main-icon.is-animating.icon-chart-grow svg path:nth-child(4)
    animation-delay: $nav-part-stagger * 3

  // --- Quadrantes/células com pop (category, sheet): 4 paths
  ::v-deep .navbar-main-icon.is-animating.icon-category svg path,
  ::v-deep .navbar-main-icon.is-animating.icon-sheet svg path
    transform-origin: center
    animation: az-icon-part-pop $nav-part-duration cubic-bezier(.34, 1.56, .64, 1) both
  ::v-deep .navbar-main-icon.is-animating.icon-category svg path:nth-child(2),
  ::v-deep .navbar-main-icon.is-animating.icon-sheet svg path:nth-child(2)
    animation-delay: $nav-part-stagger
  ::v-deep .navbar-main-icon.is-animating.icon-category svg path:nth-child(3),
  ::v-deep .navbar-main-icon.is-animating.icon-sheet svg path:nth-child(3)
    animation-delay: $nav-part-stagger * 2
  ::v-deep .navbar-main-icon.is-animating.icon-category svg path:nth-child(4),
  ::v-deep .navbar-main-icon.is-animating.icon-sheet svg path:nth-child(4)
    animation-delay: $nav-part-stagger * 3

  // --- Pessoas surgindo — BONECO INTEIRO por vez: cabeça+corpo com o MESMO
  // delay (senão aparece meio boneco). Ordem dos paths confirmada nos SVGs:
  //   people-profile (6 paths/3 bonecos): esq(3,4) centro(5,6) dir(1,2)
  //   users (4 paths/2 bonecos): frente(1,2) trás(3,4)
  ::v-deep .navbar-main-icon.is-animating.icon-people-profile svg path,
  ::v-deep .navbar-main-icon.is-animating.icon-users svg path
    transform-origin: center
    animation: az-icon-rise-fade $nav-part-duration cubic-bezier(.22, 1, .4, 1) both
  // people-profile: esquerda (3,4) → centro (5,6) → direita (1,2)
  ::v-deep .navbar-main-icon.is-animating.icon-people-profile svg path:nth-child(3),
  ::v-deep .navbar-main-icon.is-animating.icon-people-profile svg path:nth-child(4)
    animation-delay: 0s
  ::v-deep .navbar-main-icon.is-animating.icon-people-profile svg path:nth-child(5),
  ::v-deep .navbar-main-icon.is-animating.icon-people-profile svg path:nth-child(6)
    animation-delay: $nav-part-stagger
  ::v-deep .navbar-main-icon.is-animating.icon-people-profile svg path:nth-child(1),
  ::v-deep .navbar-main-icon.is-animating.icon-people-profile svg path:nth-child(2)
    animation-delay: $nav-part-stagger * 2
  // users: frente (1,2) → trás (3,4)
  ::v-deep .navbar-main-icon.is-animating.icon-users svg path:nth-child(1),
  ::v-deep .navbar-main-icon.is-animating.icon-users svg path:nth-child(2)
    animation-delay: 0s
  ::v-deep .navbar-main-icon.is-animating.icon-users svg path:nth-child(3),
  ::v-deep .navbar-main-icon.is-animating.icon-users svg path:nth-child(4)
    animation-delay: $nav-part-stagger

  // ===== Fase B — casos "leves" (2-3 partes, narrativa de montagem) =====
  // graph-pie (2 fatias): corpo (1) entra, cunha destacada (2) dá pop depois
  ::v-deep .navbar-main-icon.is-animating.icon-graph-pie svg path
    transform-origin: center
    animation: az-icon-part-pop $nav-part-duration cubic-bezier(.34, 1.56, .64, 1) both
  ::v-deep .navbar-main-icon.is-animating.icon-graph-pie svg path:nth-child(2)
    animation-delay: $nav-part-stagger * 1.5

  // chats/coin: FRENTE primeiro, trás depois. chats frente=path1; coin frente(moeda)=path2.
  ::v-deep .navbar-main-icon.is-animating.icon-chats svg path,
  ::v-deep .navbar-main-icon.is-animating.icon-coin svg path
    transform-origin: center
    animation: az-icon-rise-fade $nav-part-duration cubic-bezier(.22, 1, .4, 1) both
  ::v-deep .navbar-main-icon.is-animating.icon-chats svg path:nth-child(1),
  ::v-deep .navbar-main-icon.is-animating.icon-coin svg path:nth-child(2)
    animation-delay: 0s
  ::v-deep .navbar-main-icon.is-animating.icon-chats svg path:nth-child(2),
  ::v-deep .navbar-main-icon.is-animating.icon-coin svg path:nth-child(1)
    animation-delay: $nav-part-stagger * 1.5

  // calendar/calendar-check: cabeçalho cai do topo; corpo "baixa" (scaleY do
  // topo); no calendar-check o check estoura por último.
  // calendar = [header(1), corpo(2)]; calendar-check = [header(1), check(2), corpo(3)]
  ::v-deep .navbar-main-icon.is-animating.icon-calendar svg path:nth-child(1),
  ::v-deep .navbar-main-icon.is-animating.icon-calendar-check svg path:nth-child(1)
    transform-origin: top center
    animation: az-icon-rise-fade $nav-part-duration cubic-bezier(.22, 1, .4, 1) both
  // corpo: desce preenchendo (scaleY do topo) logo após o header
  ::v-deep .navbar-main-icon.is-animating.icon-calendar svg path:nth-child(2),
  ::v-deep .navbar-main-icon.is-animating.icon-calendar-check svg path:nth-child(3)
    transform-origin: top center
    animation: az-icon-drop-down $nav-part-duration cubic-bezier(.22, 1, .4, 1) both
    animation-delay: $nav-part-stagger
  // o check (path 2 do calendar-check) estoura por último, com pop
  ::v-deep .navbar-main-icon.is-animating.icon-calendar-check svg path:nth-child(2)
    transform-origin: center
    animation: az-icon-pop-tick $nav-tell-tick ease both
    animation-delay: $nav-part-stagger * 2.5

  // presentation-chart (3 partes: régua topo / linha / tela) → cascata
  ::v-deep .navbar-main-icon.is-animating.icon-presentation-chart svg path
    transform-origin: center
    animation: az-icon-part-pop $nav-part-duration cubic-bezier(.34, 1.56, .64, 1) both
  ::v-deep .navbar-main-icon.is-animating.icon-presentation-chart svg path:nth-child(2)
    animation-delay: $nav-part-stagger
  ::v-deep .navbar-main-icon.is-animating.icon-presentation-chart svg path:nth-child(3)
    animation-delay: $nav-part-stagger * 2

  // ===== Fase C — ícones migrados de mdi → SVG =====
  // BI (mdi-view-dashboard): 4 painéis com pop escalonado
  ::v-deep .navbar-main-icon.is-animating.icon-mdi-view-dashboard svg path
    transform-origin: center
    animation: az-icon-part-pop $nav-part-duration cubic-bezier(.34, 1.56, .64, 1) both
  ::v-deep .navbar-main-icon.is-animating.icon-mdi-view-dashboard svg path:nth-child(2)
    animation-delay: $nav-part-stagger
  ::v-deep .navbar-main-icon.is-animating.icon-mdi-view-dashboard svg path:nth-child(3)
    animation-delay: $nav-part-stagger * 2
  ::v-deep .navbar-main-icon.is-animating.icon-mdi-view-dashboard svg path:nth-child(4)
    animation-delay: $nav-part-stagger * 3

  // Data room (mdi-database): 3 discos empilham de baixo (3) para cima (1)
  ::v-deep .navbar-main-icon.is-animating.icon-mdi-database svg path
    transform-origin: center
    animation: az-icon-rise-fade $nav-part-duration cubic-bezier(.22, 1, .4, 1) both
  ::v-deep .navbar-main-icon.is-animating.icon-mdi-database svg path:nth-child(3)
    animation-delay: 0s
  ::v-deep .navbar-main-icon.is-animating.icon-mdi-database svg path:nth-child(2)
    animation-delay: $nav-part-stagger
  ::v-deep .navbar-main-icon.is-animating.icon-mdi-database svg path:nth-child(1)
    animation-delay: $nav-part-stagger * 2

  // Wealth Service (hierarchy): 4 paths = 3 nós (1,2,3) + conector (4) em pop sequencial.
  ::v-deep .navbar-main-icon.is-animating.icon-hierarchy svg path
    transform-origin: center
    animation: az-icon-part-pop $nav-part-duration cubic-bezier(.34, 1.56, .64, 1) both
  ::v-deep .navbar-main-icon.is-animating.icon-hierarchy svg path:nth-child(2)
    animation-delay: $nav-part-stagger
  ::v-deep .navbar-main-icon.is-animating.icon-hierarchy svg path:nth-child(3)
    animation-delay: $nav-part-stagger * 2
  ::v-deep .navbar-main-icon.is-animating.icon-hierarchy svg path:nth-child(4)
    animation-delay: $nav-part-stagger * 3

  // CRM (crm): rede de relacionamento "montando" — nós surgem com pop e os
  // conectores se "desenham" ligando-os, em sequência (nó topo → link esq → nó
  // inf-esq → link dir → nó inf-dir). Estrutura: 2 <line>.crm-link + 3
  // <circle>.crm-node. Conectores SEMPRE visíveis em repouso (dashoffset 0);
  // re-traçam só no hover/seleção (nth-of-type separa por tipo: line × circle).
  ::v-deep .navbar-main-icon.icon-crm svg .crm-link
    stroke-dasharray: $nav-crm-link-length
    stroke-dashoffset: 0
  ::v-deep .navbar-main-icon.is-animating.icon-crm svg .crm-node
    transform-box: fill-box
    transform-origin: center
    animation: az-icon-part-pop $nav-part-duration cubic-bezier(.34, 1.56, .64, 1) both
  ::v-deep .navbar-main-icon.is-animating.icon-crm svg .crm-link
    animation: az-crm-link-draw $nav-crm-draw ease backwards
  ::v-deep .navbar-main-icon.is-animating.icon-crm svg .crm-link:nth-of-type(1)
    animation-delay: $nav-part-stagger
  ::v-deep .navbar-main-icon.is-animating.icon-crm svg .crm-node:nth-of-type(2)
    animation-delay: $nav-part-stagger * 1.5
  ::v-deep .navbar-main-icon.is-animating.icon-crm svg .crm-link:nth-of-type(2)
    animation-delay: $nav-part-stagger * 2.5
  ::v-deep .navbar-main-icon.is-animating.icon-crm svg .crm-node:nth-of-type(3)
    animation-delay: $nav-part-stagger * 3

  // Financial Planning (mdi-chart-timeline-variant): linha "desenhada" via
  // stroke-dashoffset + nós surgindo. Ícone SEMPRE visível (base dashoffset 0);
  // re-traça só no hover/seleção.
  ::v-deep .navbar-main-icon.icon-mdi-chart-timeline-variant svg .chart-line
    stroke-dasharray: $nav-chart-line-length
    stroke-dashoffset: 0
  ::v-deep .navbar-main-icon.is-animating.icon-mdi-chart-timeline-variant svg .chart-line
    animation: az-chart-draw $nav-chart-draw ease
  ::v-deep .navbar-main-icon.is-animating.icon-mdi-chart-timeline-variant svg .chart-node
    transform-box: fill-box
    transform-origin: center
    animation: az-icon-part-pop $nav-chart-node ease backwards
  ::v-deep .navbar-main-icon.is-animating.icon-mdi-chart-timeline-variant svg .chart-node:nth-of-type(1)
    animation-delay: $nav-chart-node-step
  ::v-deep .navbar-main-icon.is-animating.icon-mdi-chart-timeline-variant svg .chart-node:nth-of-type(2)
    animation-delay: $nav-chart-node-step * 3
  ::v-deep .navbar-main-icon.is-animating.icon-mdi-chart-timeline-variant svg .chart-node:nth-of-type(3)
    animation-delay: $nav-chart-node-step * 5
  ::v-deep .navbar-main-icon.is-animating.icon-mdi-chart-timeline-variant svg .chart-node:nth-of-type(4)
    animation-delay: $nav-chart-node-step * 7

  // Requisições (mdi-clipboard-list-outline): path único → bloco (bob)
  ::v-deep .navbar-main-icon.is-animating.icon-mdi-clipboard-list-outline svg
    animation: az-icon-bob $nav-tell-bob ease

  // Solicitações (clipboard-text): 2 paths → bloco (bob), consistente com o
  // clipboard das Requisições. O scale no hover já vem do .navbar-main-icon.
  ::v-deep .navbar-main-icon.is-animating.icon-clipboard-text svg
    animation: az-icon-bob $nav-tell-bob ease

.button-navbar-size-sm
  height: 36px
  width: 236px

  p
    font-size: 12px

.button-navbar-size-default
  height: 52px
  width: 260px

  p
    font-size: 14px

.button-navbar-size-hidden
  height: 52px
  width: 56px

// respeita usuários que pedem menos movimento no SO
@media (prefers-reduced-motion: reduce)
  .button-navbar ::v-deep .navbar-main-icon,
  .button-navbar ::v-deep .navbar-main-icon svg,
  .button-navbar ::v-deep .navbar-main-icon svg path,
  .button-navbar ::v-deep .navbar-main-icon svg .chart-line,
  .button-navbar ::v-deep .navbar-main-icon svg .chart-node,
  .button-navbar ::v-deep .navbar-main-icon svg .crm-link,
  .button-navbar ::v-deep .navbar-main-icon svg .crm-node
    animation: none !important
    transition: none !important
  // sem animação a máscara do donut ficaria em 0deg (escondendo) — remove p/ aparecer normal
  .button-navbar ::v-deep .navbar-main-icon.icon-donut
    -webkit-mask: none !important
    mask: none !important
</style>
